import { Component, h, Host, Prop, Event, EventEmitter } from "@stencil/core";
import {
    handleOuterNavigation,
    handleOuterDropdownContainer,
} from "./navigation-controller/navigation-controller";
import applyElementPolyfills from "../../polyfills/element";
import "../../polyfills/node-list";
import { debounce } from "../../utils/debounce";
import waitForHydrated from "../../utils/waitForHydrated";
import { desktopBreakpoint } from "./shared-variables";

applyElementPolyfills();

@Component({
    tag: "ux-iws-nav",
    styleUrl: "navigation.scss",
    shadow: false,
})
export class Navigation {
    private baseClass: string = "ux-iws-nav";
    private baseElement: HTMLElement = document.querySelector(this.baseClass);
    private level1: HTMLUListElement | null = null;
    private level2: NodeListOf<HTMLUListElement> | null = null;
    private level3: NodeListOf<HTMLUListElement> | null = null;
    private level1Items: NodeListOf<HTMLElement> | null = null;
    private level2Items: NodeListOf<HTMLElement> | null = null;
    private level3Wrapper: NodeListOf<HTMLElement> | null = null;
    private overviewLinks: NodeListOf<HTMLElement> | null = null;
    private subHeadlines: NodeListOf<HTMLElement> | null = null;
    private activeElement: HTMLElement | null = null;
    private lvl2Back: NodeListOf<HTMLElement> | null = null;
    private lvl3Back: NodeListOf<HTMLElement> | null = null;
    private toolboxWrapper: NodeListOf<HTMLElement> | null = null;

    /** Back button text */
    @Prop() backButtonText: string = "Back";

    /** Navigation level change event */
    @Event({
        eventName: "uxIwsNavLevelChange",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    navLevelChange: EventEmitter;

    /** emits calculated height of highest dropdown navigation */
    @Event({
        eventName: "uxIwsNavHighestSubNavigation",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    navHighestSubNavigation: EventEmitter;

    /** emits local selection */
    @Event({
        eventName: "uxNavigationLocaleSelection",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    navigationLocaleSelectionOpen: EventEmitter;

    componentDidLoad() {
        this.initStaticElements();
        this.addDynamicElements();
        this.addDynamicClasses();
        this.setStaticEvents();
        this.handleOverviewLinks();
        this.resizer();
    }

    /**
     * Init static elements
     */
    private initStaticElements = () => {
        this.level1 = this.baseElement.querySelector(".ux-iws-nav__lvl1");
        this.level2 = this.baseElement.querySelectorAll(".ux-iws-nav__lvl2");
        this.level3 = this.baseElement.querySelectorAll(".ux-iws-nav__lvl3");
        this.level1Items = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl1-item"
        );
        this.level2Items = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl2-item"
        );
        this.level3Wrapper = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl3-wrapper"
        );
        this.overviewLinks = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl3-item--overview"
        );
        this.subHeadlines = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl3-item--subheadline"
        );
        this.toolboxWrapper = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl2-item-toolbox"
        );
        this.activeElement = this.level1;
    };

    /**
     * to keep the navigation html structure as simple as possible, additional elements are added dynamically
     */
    private addDynamicElements = () => {
        /**
         * add "go back" arrows and functionality for mobile navigation
         */
        this.level2.forEach((item) => {
            const mobileBack = document.createElement("span");
            mobileBack.className = "ux-iws-nav__lvl2-back";
            mobileBack.textContent = this.backButtonText;
            item.prepend(mobileBack);
        });

        this.level3Wrapper.forEach((item) => {
            const mobileBack = document.createElement("span");
            mobileBack.className = "ux-iws-nav__lvl3-back";
            mobileBack.textContent = this.backButtonText;
            item.prepend(mobileBack);
        });

        this.lvl2Back = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl2-back"
        );
        this.lvl3Back = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl3-back"
        );

        /**
         * add arrow icon for level 1 items that contain children, displayed on mobile
         */
        this.level1Items.forEach((item) => {
            if (item.children.length > 1) {
                const arrowIcon = document.createElement("ux-icon");
                arrowIcon.setAttribute("name", "chevron-right");
                arrowIcon.setAttribute("weight", "normal");
                item.firstElementChild.append(arrowIcon);
            }
        });

        /**
         * add arrow icon for level 2 items that contain children
         */
        this.level2Items.forEach((item) => {
            if (item.children.length > 1) {
                const arrowIcon = document.createElement("ux-icon");
                arrowIcon.setAttribute("name", "chevron-right");
                arrowIcon.setAttribute("weight", "normal");
                item.firstElementChild.append(arrowIcon);
            }
        });
    };

    /**
     * to keep the navigation structure as simple as possible, additional classes are added dynamically
     * depending on the number of subitems respective classes are defined
     */
    private addDynamicClasses = () => {
        this.level1Items.forEach((item) => {
            if (item.children.length > 1) {
                item.classList.add("ux-iws-nav__lvl1-item--has-children");
            }
        });

        this.level2Items.forEach((item) => {
            if (item.children.length > 1) {
                item.classList.add("ux-iws-nav__lvl2-item--has-children");
            }
        });
    };

    /**
     * Static navigation event handling
     */
    private setStaticEvents = () => {
        this.level1Items.forEach((item) => {
            item.addEventListener("click", this.navigationForth);
        });
        this.setKeyboardNavigationEvents(this.level1Items);
        this.setKeyboardNavigationEvents(this.level2Items);
        this.level2.forEach((item) => {
            item.addEventListener("click", this.stopBubbling);
        });
        this.level3Wrapper.forEach((item) => {
            item.addEventListener("click", this.stopBubbling);
        });
        this.level3.forEach((item) => {
            item.addEventListener("click", this.stopBubbling);
        });
        this.subHeadlines.forEach((item) => {
            item.addEventListener("click", this.preventDefault);
        });
        this.lvl2Back.forEach((item) => {
            item.addEventListener("click", this.navigationBack);
        });
        this.lvl3Back.forEach((item) => {
            item.addEventListener("click", this.navigationBack);
        });
        this.baseElement.addEventListener("uxIwsNavOpen", () => {
            this.navigationOpen();
        });
        this.baseElement.addEventListener("uxIwsNavClose", () => {
            this.navigationClose();
        });
        this.baseElement.addEventListener(
            "uxToolboxLoaded",
            (event: CustomEvent) => {
                if (event.detail.success == true) {
                    if (window.innerWidth < desktopBreakpoint) {
                        this.setHighestSubNavigation();
                    }
                    const teaserIcon = this.activeElement.querySelector(
                        "ux-iws-teaser-sidebar-icon:last-child"
                    ) as HTMLElement;
                    waitForHydrated(teaserIcon, () => {
                        handleOuterDropdownContainer(
                            this.activeElement.offsetHeight
                        );
                    });
                }
            }
        );

        document.body.addEventListener("click", (e) => {
            const target = e.target as Node;
            const header = this.baseElement.closest(
                ".ux-iws-header"
            ) as HTMLElement;
            if (header && !header.contains(target)) {
                this.navigationClose();
            }
        });
        window.addEventListener("resize", this.resizer);
    };

    /**
     * Accessibility
     * Add event handling for keyboard
     */
    private setKeyboardNavigationEvents = (level: NodeListOf<HTMLElement>) => {
        level.forEach((item) => {
            if (item.children.length > 1) {
                item.querySelector("a").addEventListener(
                    "click",
                    (e: Event) => {
                        e.preventDefault();
                        this.navigationForth(e);
                    }
                );
            }
        });
    };

    /**
     * Dynamic navigation event handling
     * Level 3 navigation should open on click for mobile and desktop
     */
    private setDynamicEvents = () => {
        this.level2Items.forEach((item) => {
            item.removeEventListener("click", this.navigationForth);
        });

        if (window.innerWidth < desktopBreakpoint) {
            this.level2Items.forEach((item) => {
                item.addEventListener("click", this.navigationForth);
            });
        } else {
            this.level2Items.forEach((item) => {
                item.addEventListener("click", this.navigationForth);
            });
        }
    };

    /**
     * Prevent Default
     */
    private preventDefault = (e: Event) => {
        e.preventDefault();
    };

    /**
     * Stop bubbling
     */
    private stopBubbling = (e: Event) => {
        e.stopPropagation();
    };

    /**
     * Navigation dive in
     */
    private navigationForth = (e: Event) => {
        let target = e.target as HTMLElement;
        if (!target) {
            return;
        }
        if (target.tagName === "A") {
            target = target.parentElement;
        }
        const currentLevel = target.parentElement.className.includes("lvl1")
            ? "2"
            : "3";
        const targetChildQuerySelector =
            currentLevel == "2"
                ? ".ux-iws-nav__lvl2"
                : ".ux-iws-nav__lvl3-wrapper";
        const targetChild = target.querySelector(
            targetChildQuerySelector
        ) as HTMLElement;
        if (target.className.includes("has-children")) {
            this.activeElement = targetChild;
            this.stopBubbling(e);
            this.handleActiveStateClasses(target, currentLevel, "add");
            this.handleToolbox(currentLevel, "open");
            if (window.innerWidth < desktopBreakpoint) {
                this.setHighestSubNavigation();
            }
            handleOuterNavigation(currentLevel, "open");
            handleOuterDropdownContainer(this.activeElement.offsetHeight);
            this.windowScrollTop();
            this.navLevelChange.emit({
                currentLevel: currentLevel,
            });
        } else {
            const backButton = target.parentElement.parentElement.querySelector(
                ".ux-iws-nav__lvl3-back"
            ) as HTMLElement;
            if (currentLevel == "3" && backButton !== null) {
                backButton.click();
            }
        }
    };

    /**
     * Mobile menu go step back level 2
     */
    private navigationBack = (e) => {
        const target = e.target as HTMLElement;
        const targetParent = target.parentElement.parentElement
            .parentElement as HTMLElement;
        const currentLevel = targetParent.className.includes("ux-iws-nav__lvl1")
            ? "1"
            : "2";
        this.activeElement = targetParent;
        this.handleActiveStateClasses(target, currentLevel, "remove");
        this.handleToolbox(currentLevel, "close");
        if (window.innerWidth < desktopBreakpoint) {
            this.setHighestSubNavigation();
        }
        handleOuterNavigation(currentLevel, "open");
        handleOuterDropdownContainer(this.activeElement.offsetHeight);
        this.navLevelChange.emit({
            currentLevel: currentLevel,
        });
    };

    /**
     * Open navigation
     */
    private navigationOpen = () => {
        const currentLevel = "1";
        this.activeElement = this.level1;
        handleOuterNavigation(currentLevel, "open");
        this.windowScrollTop();
    };

    /**
     * Close navigation
     */
    private navigationClose = () => {
        const currentLevel = "1";
        this.activeElement = this.level1;
        this.handleActiveStateClasses(null, currentLevel, "remove");
        handleOuterNavigation(currentLevel, "close");
        this.navigationLocaleSelectionOpen.emit({ isOpen: false });
    };

    /**
     * handle active state classes
     * @param target
     * @param currentLevel
     * @param type
     */
    private handleActiveStateClasses = (target, currentLevel, type) => {
        this.level2Items.forEach((item) => {
            item.classList.remove("ux-iws-nav--active");
            item.classList.remove("ux-iws-nav--not-active");
        });
        if (type == "add") {
            if (currentLevel == "2") {
                this.level1Items.forEach((item) => {
                    item.classList.remove("ux-iws-nav--active");
                    item.classList.add("ux-iws-nav--not-active");
                });
            } else {
                target.parentElement
                    .querySelectorAll(".ux-iws-nav__lvl2-item")
                    .forEach((item) => {
                        item.classList.add("ux-iws-nav--not-active");
                    });
            }
            target.classList.remove("ux-iws-nav--not-active");
            target.classList.add("ux-iws-nav--active");
        } else {
            if (currentLevel == "1") {
                this.level1Items.forEach((item) => {
                    item.classList.remove("ux-iws-nav--active");
                    item.classList.remove("ux-iws-nav--not-active");
                });
            }
        }
    };

    /**
     * Set desktop level 2 and level 3 navigation height based on the element with the most subitems
     * if toolbox content is higher than navigation content, use toolbox inner height
     */
    private setHighestSubNavigation = () => {
        const navigationHeightArray = [];
        const toolboxItems = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl2-item-toolbox"
        );
        const hasToolbox = toolboxItems.length > 0;
        const headerHeight = 100;
        const toolboxGutter = 35;
        const toolboxHeightNoGutter = 125;

        this.level2.forEach((item) => {
            item.style.height = "auto";
            navigationHeightArray.push(item.offsetHeight);
        });
        this.level3.forEach((item) => {
            item.style.height = "auto";
            const height = hasToolbox
                ? item.offsetHeight + toolboxHeightNoGutter
                : item.offsetHeight;
            navigationHeightArray.push(height);
        });
        toolboxItems.forEach((toolboxItem: HTMLUListElement) => {
            toolboxItem.style.marginTop = "0";
        });
        const highestNavigation = Math.max(...navigationHeightArray);
        this.navHighestSubNavigation.emit(highestNavigation);
        /** navigationFits = the viewport is tall enought that
         * all the navigation points can be displayed with the same height */
        const navigationFits =
            highestNavigation + headerHeight < window.innerHeight;
        this.level2.forEach((item) => {
            if (window.innerWidth < desktopBreakpoint) {
                item.style.height = "auto";
            } else {
                if (navigationFits) {
                    item.style.height = highestNavigation + "px";
                } else {
                    const itemLevel3 = item.querySelector(
                        ".ux-iws-nav__lvl3"
                    ) as HTMLElement;
                    const hasLevel3 = !!itemLevel3;
                    const highestHeight = hasLevel3
                        ? Math.max(
                              itemLevel3.offsetHeight + toolboxHeightNoGutter,
                              item.offsetHeight
                          )
                        : item.offsetHeight;
                    if (highestHeight + headerHeight > window.innerHeight) {
                        item.style.height =
                            window.innerHeight - headerHeight + "px";
                        item.style.overflowY = "auto";
                        item.style.overflowX = "hidden";
                    } else {
                        item.style.height = highestHeight + "px";
                    }
                }
            }
        });
        this.level3.forEach((item) => {
            if (window.innerWidth < desktopBreakpoint) {
                item.style.height = "auto";
            } else {
                if (navigationFits) {
                    const height = hasToolbox
                        ? highestNavigation - toolboxHeightNoGutter
                        : highestNavigation;
                    item.style.height = height + "px";
                } else {
                    const lv2Ul = item.closest(
                        ".ux-iws-nav__lvl2"
                    ) as HTMLElement;
                    const lvl2Height = hasToolbox
                        ? lv2Ul.scrollHeight - toolboxHeightNoGutter
                        : lv2Ul.scrollHeight;
                    const siblings = Array.from(
                        lv2Ul.querySelectorAll(".ux-iws-nav__lvl3")
                    );
                    const heights = [];
                    siblings.forEach((sibling: HTMLElement) =>
                        heights.push(sibling.offsetHeight)
                    );
                    const maxHeight = Math.max(lvl2Height, ...heights);
                    item.style.height = maxHeight + "px";
                }
            }
        });
        /** managing the margin top of each toolbox when the navigation fits */
        if (
            hasToolbox &&
            navigationFits &&
            window.innerWidth >= desktopBreakpoint
        ) {
            toolboxItems.forEach((toolboxItem: HTMLElement) => {
                const toolboxRects = toolboxItem.getBoundingClientRect();
                const toolboxParentRects =
                    toolboxItem.parentElement.getBoundingClientRect();
                if (toolboxParentRects.bottom - toolboxRects.bottom > 1) {
                    toolboxItem.style.marginTop =
                        toolboxParentRects.bottom - toolboxRects.bottom + "px";
                }
            });
        } else if (
            hasToolbox &&
            !navigationFits &&
            window.innerWidth >= desktopBreakpoint
        ) {
            toolboxItems.forEach((toolboxItem: HTMLElement) => {
                const parentScrollHeight =
                    toolboxItem.parentElement.scrollHeight;
                const level3ScrollHeight =
                    toolboxItem.parentElement.querySelector(
                        ".ux-iws-nav__lvl3"
                    )?.scrollHeight;
                const highestScrollHeight =
                    level3ScrollHeight >
                    parentScrollHeight - toolboxHeightNoGutter
                        ? level3ScrollHeight + toolboxHeightNoGutter
                        : parentScrollHeight;
                const toolboxOffsetTop = toolboxItem.offsetTop;
                const marginTop =
                    highestScrollHeight -
                    toolboxOffsetTop -
                    toolboxHeightNoGutter -
                    toolboxGutter;
                toolboxItem.style.marginTop = marginTop + "px";
            });
        }
    };

    /**
     * Handle positioning of overview Links
     */
    private handleOverviewLinks = () => {
        this.overviewLinks.forEach((item) => {
            item.closest(".ux-iws-nav__lvl3-wrapper").classList.add(
                "ux-iws-nav__lvl3-wrapper--has-overview-links"
            );
        });
    };

    /**
     * Load toolbox
     * Hide toolbox level 3 mobile
     */
    private handleToolbox = (currentLevel, type) => {
        if (type == "open") {
            if (currentLevel == "2") {
                const activeToolbox = this.activeElement.querySelector(
                    ".ux-iws-meta-navigation-toolbox"
                );
                if (activeToolbox !== null) {
                    activeToolbox.setAttribute("active", "true");
                }

                this.toolboxWrapper.forEach((item) => {
                    item.classList.add("ux-iws-nav__lvl2-item-toolbox--show");
                });
            } else {
                this.toolboxWrapper.forEach((item) => {
                    item.classList.remove(
                        "ux-iws-nav__lvl2-item-toolbox--show"
                    );
                });
            }
        } else {
            if (currentLevel == "1") {
                this.toolboxWrapper.forEach((item) => {
                    item.classList.remove(
                        "ux-iws-nav__lvl2-item-toolbox--show"
                    );
                });
            } else {
                this.toolboxWrapper.forEach((item) => {
                    item.classList.add("ux-iws-nav__lvl2-item-toolbox--show");
                });
            }
        }
    };

    /**
     * Always scroll to top on navigation open
     */
    private windowScrollTop = () => {
        window.scrollTo(0, 0);
    };

    /**
     * browser resizing
     * used for different event handling mobile vs. desktop
     * used for level 2 and level 3 navigation height
     * used for dropdown container vertical offset
     * used for navigation close level 1 on desktop
     * debounced resizer -> 10ms
     */
    private resizer = debounce(() => {
        this.setDynamicEvents();
        this.setHighestSubNavigation();
        handleOuterDropdownContainer(this.activeElement.offsetHeight);

        if (
            document.body.classList.contains("ux-iws-nav-active--lvl-1") ==
                true &&
            window.innerWidth >= desktopBreakpoint
        ) {
            this.navigationClose();
        }
    }, 10);

    /**
     * Render navigation
     */
    render() {
        return (
            <Host class={`${this.baseClass}`}>
                <slot name="navigation" />
                <slot name="navigation-meta" />
                <slot name="navigation-eyecatcher" />
            </Host>
        );
    }
}
